import React, { useState, useEffect } from 'react';

const Tour = ({ steps, isOpen, onClose, tourKey }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);

  useEffect(() => {
    if (!isOpen || !steps[currentStep]) return;

    const updatePosition = () => {
      const targetSelector = steps[currentStep].target;
      const element = document.querySelector(targetSelector);
      
      if (element) {
        const rect = element.getBoundingClientRect();
        setTargetRect({
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height
        });
        
        // Scroll element into view if needed
        if (rect.top < 100 || rect.bottom > window.innerHeight - 100) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      } else {
        setTargetRect(null);
      }
    };

    updatePosition();
    
    // Update on scroll/resize
    window.addEventListener('scroll', updatePosition);
    window.addEventListener('resize', updatePosition);
    
    return () => {
      window.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
    };
  }, [currentStep, isOpen, steps]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleFinish();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleFinish = () => {
    localStorage.setItem(`tour_completed_${tourKey}`, 'true');
    setCurrentStep(0);
    onClose();
  };

  const handleSkip = () => {
    localStorage.setItem(`tour_completed_${tourKey}`, 'true');
    setCurrentStep(0);
    onClose();
  };

  if (!isOpen || !steps[currentStep]) return null;

  const step = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;
  const isFirstStep = currentStep === 0;

  // Calculate tooltip position based on target
  const getTooltipPosition = () => {
    if (!targetRect) {
      return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
    }

    const placement = step.placement || 'bottom';
    const tooltipWidth = 380;
    const padding = 16;
    
    // Calculate left position - keep tooltip on screen
    let left = targetRect.left;
    if (left + tooltipWidth > window.innerWidth - padding) {
      left = window.innerWidth - tooltipWidth - padding;
    }
    if (left < padding) {
      left = padding;
    }

    if (placement === 'bottom') {
      return {
        top: targetRect.top + targetRect.height + 16,
        left: left
      };
    } else if (placement === 'top') {
      // Position tooltip above the target - estimate tooltip height ~250px
      const tooltipTop = targetRect.top - 260;
      return {
        top: Math.max(16, tooltipTop),
        left: left
      };
    }
    
    return { top: targetRect.top + targetRect.height + 16, left: left };
  };

  const tooltipPos = getTooltipPosition();

  return (
    <>
      {/* Semi-transparent overlay */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.4)',
          zIndex: 10000
        }}
        onClick={handleSkip}
      />

      {/* Spotlight border - transparent with dark grey border */}
      {targetRect && (
        <div
          style={{
            position: 'fixed',
            top: targetRect.top - 8,
            left: targetRect.left - 8,
            width: targetRect.width + 16,
            height: targetRect.height + 16,
            borderRadius: '8px',
            border: '3px solid #333',
            background: 'transparent',
            zIndex: 10001,
            pointerEvents: 'none'
          }}
        />
      )}

      {/* Tooltip */}
      <div 
        style={{
          position: 'fixed',
          top: typeof tooltipPos.top === 'string' ? tooltipPos.top : tooltipPos.top,
          left: typeof tooltipPos.left === 'string' ? tooltipPos.left : tooltipPos.left,
          transform: tooltipPos.transform || 'none',
          zIndex: 10002,
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          maxWidth: '380px',
          width: 'calc(100% - 32px)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
        }}
      >
        {/* Step indicator */}
        <div style={{ 
          fontSize: '12px', 
          color: '#1976d2', 
          marginBottom: '8px',
          fontWeight: '600'
        }}>
          Step {currentStep + 1} of {steps.length}
        </div>

        {/* Title */}
        <h3 style={{ 
          margin: '0 0 12px 0', 
          fontSize: '20px', 
          color: '#1d2934',
          fontWeight: '600'
        }}>
          {step.title}
        </h3>

        {/* Content */}
        <p style={{ 
          margin: '0 0 24px 0', 
          color: '#5e778a', 
          lineHeight: '1.6',
          fontSize: '15px'
        }}>
          {step.content}
        </p>

        {/* Navigation buttons */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <button
            onClick={handleSkip}
            style={{
              background: 'none',
              border: 'none',
              color: '#5e778a',
              cursor: 'pointer',
              fontSize: '13px',
              padding: '8px 0'
            }}
          >
            Skip tour
          </button>

          <div style={{ display: 'flex', gap: '8px' }}>
            {!isFirstStep && (
              <button
                onClick={handlePrev}
                style={{
                  padding: '10px 18px',
                  borderRadius: '6px',
                  border: '1px solid #e0e0e0',
                  background: 'white',
                  color: '#666',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Back
              </button>
            )}
            <button
              onClick={handleNext}
              style={{
                padding: '10px 24px',
                borderRadius: '6px',
                border: 'none',
                background: '#1976d2',
                color: 'white',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600'
              }}
            >
              {isLastStep ? 'Finish' : 'Next'}
            </button>
          </div>
        </div>

        {/* Progress dots */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          gap: '6px', 
          marginTop: '20px' 
        }}>
          {steps.map((_, idx) => (
            <div
              key={idx}
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: idx === currentStep ? '#1976d2' : '#e0e0e0',
                transition: 'background 0.2s'
              }}
            />
          ))}
        </div>
      </div>
    </>
  );
};

export default Tour;